/**
 * M9 CH-12b: the hulk screens as view models (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §4.5): the survey
 * card and the kit progress, in plain words, with no counters beyond the kit's delivered / needed (like M7).
 *
 * - **The card** (`hulkScreenView`) wraps CH-4's `hulkCardModel` (title, flaw lines, the First Rush edge, the claimed
 *   value, hazards and squatters with their state, the kit table) and adds the next step in words and the buttons
 *   the Reach modal offers: *Start the claim* before the kit is open, *Abandon the claim* while it is, and *Close*.
 * - **The kit progress** (`hulkKitProgress`): one row per good with delivered / needed and what the hold carries,
 *   and whether the kit is complete.
 * - **The modal** (`hulkCardModal`): a Reach `ModalSpec` (`reach/ui/modal.ts showModal`) of the card. CH-12a shows it
 *   when the wreck scan of a hulk surveys it, and maps the chosen button id (`HULK_CARD_BUTTON`) to the fleet
 *   service (`openHulkClaim`, `closeHulkClaim`).
 * - **Refusal words** for CH-5b's kit (`HULK_KIT_REFUSAL_TEXT`), for the prompts and toasts.
 *
 * Pure; UI-rate (allocates), never per frame.
 */
import type { Career } from '../../career/types'
import { hulksConfig, type HulksConfig } from '../../hulks/data'
import { claimMaxOpen, openHulkCount, type HulkKitRefusal } from '../../hulks/kit'
import { fillTokens } from '../../story-link/world'
import { hulkCardModel, hulkKitRows, type HulkSurveyCard } from '../../hulks/survey'
import type { ModalButton, ModalSpec } from '../../reach/ui/modal'

/** The button ids of the hulk card modal (CH-12a maps them to the fleet service). */
export const HULK_CARD_BUTTON = Object.freeze({
  /** `FleetService.openHulkClaim(key)`. */
  startKit: 'hulkStartKit',
  /** `FleetService.closeHulkClaim(key)`. */
  abandon: 'hulkAbandon',
  close: 'close',
} as const)
export type HulkCardButtonId = (typeof HULK_CARD_BUTTON)[keyof typeof HULK_CARD_BUTTON]

/** The hulk screens' words. */
export const HULK_UI_TEXT = Object.freeze({
  startKit: 'Start the claim',
  abandon: 'Abandon the claim',
  close: 'Close',
  kitComplete: 'The kit is complete.',
  kitHeader: 'Kit',
  steps: {
    vent: 'Next: vent the coolant leak. Cut in from a safe distance.',
    clear: 'Next: deal with the squatters. Fight them off, or pay them to leave.',
    startKit: 'Next: start the claim, then bring the kit goods.',
    deliver: 'Next: deliver the kit goods from your hold at the hulk.',
    claim: 'Next: claim the hull. It flies home to your dock.',
  },
} as const)

/** Plain words for each of CH-5b's kit refusals. */
export const HULK_KIT_REFUSAL_TEXT: Readonly<Record<HulkKitRefusal, string>> = Object.freeze({
  noSite: 'There is no claim to work on here.',
  notSurveyed: 'Survey the hulk first (Scanner Mk II or better).',
  notEligible: 'Nothing here worth claiming.',
  tooManyOpen: 'You have as many open claims as you can run. Finish or abandon one first.',
  notOpen: 'Start the claim first.',
  hazard: 'Vent the coolant leak first.',
  squatters: 'Deal with the squatters first.',
  complete: 'The kit is complete.',
  nothingToDeliver: 'Your hold carries nothing the kit still needs.',
  notInHold: 'That part is not in your hold.',
  wrecked: 'That part is too worn to use. It must be Serviceable or better.',
  heldBack: 'That cargo is spoken for.',
  notNeeded: 'That part gives nothing the kit still needs.',
})

/** The next thing to do at a surveyed hulk. */
export type HulkStep = keyof typeof HULK_UI_TEXT.steps

/** One kit row: a good, how much is in, how much is needed, and what the hold carries. */
export interface HulkKitRowView {
  id: string
  label: string
  delivered: number
  need: number
  inHold: number
  done: boolean
}

export interface HulkKitProgressView {
  rows: HulkKitRowView[]
  /** Every row is in. */
  complete: boolean
}

export interface HulkScreenView {
  key: string
  title: string
  /** The card's text lines (CH-4's `hulkCardModel`), the kit table included. */
  lines: string[]
  /** The lines above the kit table (flaws, edge, value, hazards): what a host that draws `kit` itself shows. */
  cardLines: string[]
  kit: HulkKitProgressView
  step: HulkStep
  /** The next step, in words. */
  stepText: string
  /** The claim is open (a kit is started). */
  open: boolean
  buttons: ModalButton[]
}

/** The kit progress of a surveyed hulk: delivered / needed per good, and the hold's stock. */
export function hulkKitProgress(card: HulkSurveyCard, career: Career): HulkKitProgressView {
  const rows: HulkKitRowView[] = hulkKitRows(card, career).map((r) => ({
    id: r.id,
    label: r.label,
    delivered: Math.min(r.delivered, r.need),
    need: r.need,
    inHold: r.inHold,
    done: r.delivered >= r.need,
  }))
  return { rows, complete: rows.every((r) => r.done) }
}

/** The next step at the hulk: vent, clear the squat, start the claim, deliver, then claim. */
export function hulkNextStep(card: HulkSurveyCard, career: Career): HulkStep {
  const site = career.hulks?.sites[card.key]
  if (card.hazards.coolantHazard && site?.vented !== true) return 'vent'
  if (card.hazards.squatters && site?.cleared === undefined) return 'clear'
  if (site?.open !== true) return 'startKit'
  return hulkKitProgress(card, career).complete ? 'claim' : 'deliver'
}

/** The survey card's view: CH-4's lines, the kit progress, the next step and the modal's buttons. */
export function hulkScreenView(card: HulkSurveyCard, career: Career, cfg: HulksConfig = hulksConfig(), maxOpen: number = claimMaxOpen()): HulkScreenView {
  const model = hulkCardModel(card, career, cfg)
  const open = career.hulks?.sites[card.key]?.open === true
  const step = hulkNextStep(card, career)
  const buttons: ModalButton[] = []
  // R3-5: at the open-claims cap Start is shown disabled with the reason (and the step line says it).
  const full = !open && openHulkCount(career) >= maxOpen
  const fullText = fillTokens(cfg.text.live.tooManyOpen, { max: String(maxOpen) })
  if (!open) buttons.push(full ? { id: HULK_CARD_BUTTON.startKit, label: HULK_UI_TEXT.startKit, disabled: true, reason: fullText } : { id: HULK_CARD_BUTTON.startKit, label: HULK_UI_TEXT.startKit, primary: step === 'startKit' })
  else buttons.push({ id: HULK_CARD_BUTTON.abandon, label: HULK_UI_TEXT.abandon })
  // Close is last: Esc answers with the last button (`ModalSpec.dismissable`).
  buttons.push({ id: HULK_CARD_BUTTON.close, label: HULK_UI_TEXT.close, primary: step !== 'startKit' || full })
  const kitAt = model.lines.indexOf(cfg.text.kitHeader)
  const cardLines = kitAt < 0 ? model.lines : model.lines.slice(0, kitAt)
  return { key: card.key, title: model.title, lines: model.lines, cardLines, kit: hulkKitProgress(card, career), step, stepText: full && step === 'startKit' ? fullText : HULK_UI_TEXT.steps[step], open, buttons }
}

/** The Reach modal of the card (`showModal(root, hulkCardModal(view))`); the answer is a `HULK_CARD_BUTTON` id. */
export function hulkCardModal(view: HulkScreenView): ModalSpec {
  return { title: view.title, body: [...view.lines, '', view.stepText].join('\n'), buttons: view.buttons }
}
